import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useRmsAnalytics from '../hooks/useRmsAnalytics';
import useEmailedSignInLink from '../hooks/useEmailedSignInLink';
import { getOrgConfig } from '../utils/orgResolution';
import { LEGACY_SIGN_IN, signInPageForContinue } from '../utils/signInRoutes';
import { SIGN_IN_LINK_LIFETIME } from '../constants/signInLink';

/*
 * LinkSignInPage (/rms/link-sign-in, TDI-706 criterion 1).
 *
 * Where an emailed RMS sign-in link lands (family-portal links land on the
 * family sign-in page instead). The link's one-time token is swapped for a
 * session and the person goes on to where they were heading. A used,
 * expired or invalid link says so in the words the emailed-link step has
 * always used, then goes to the sign-in page for that address to ask for a
 * new link.
 */

const DEFAULT_CONTINUE = '/rms/dashboard';
const REDIRECT_DELAY_MS = 1500;

export default function LinkSignInPage() {
  const { orgId } = useMemo(() => getOrgConfig(), []);
  const analytics = useRmsAnalytics(orgId);
  const navigate = useNavigate();
  const { link, status } = useEmailedSignInLink({
    defaultContinue: DEFAULT_CONTINUE,
    onError: (e) => analytics.error({
      where: 'linkSignIn.redeem', code: e?.code || 'unknown', message: String(e?.message || e),
    }),
  });
  const failed = status === 'failed' || !link;

  // Same as the emailed-link step: a failed link goes on to the sign-in page
  // for the address it was heading to, to ask for a new one.
  useEffect(() => {
    if (!failed) return undefined;
    const to = signInPageForContinue(link?.continuePath) || LEGACY_SIGN_IN;
    const t = setTimeout(() => navigate(to, { replace: true }), REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [failed, link, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="w-full text-center">
          <img
            src="/td_logo_full_2025.png"
            alt="ThinkDivergent"
            className="mx-auto mb-10"
            style={{ width: '180px', height: 'auto' }}
          />
          {failed ? (
            <div role="alert">
              <h1 className="text-2xl font-bold mb-2">Sign-in Link Expired</h1>
              <p className="text-gray-600 mb-2">
                {`This sign-in link has expired or has already been used. Sign-in links can only be used once and work for ${SIGN_IN_LINK_LIFETIME}.`}
              </p>
              <p className="text-gray-600">Redirecting you to request a new sign-in link...</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-2">Signing you in</h1>
              <p className="text-gray-600 mb-4">
                This takes a few seconds. You do not need to tap the link again.
              </p>
              <div
                className="mx-auto mb-10 h-1 w-40 overflow-hidden rounded-full bg-gray-200 motion-reduce:bg-gray-300"
                role="status"
                aria-label="Signing you in"
              >
                <div className="h-full w-1/3 rounded-full bg-gray-400 animate-[verify-sweep_1.4s_ease-in-out_infinite] motion-reduce:w-full motion-reduce:animate-none" />
              </div>
            </>
          )}
          <p className="mt-6 text-xs text-gray-500">Need help? Contact the school or service that invited you.</p>
        </div>
      </div>
    </div>
  );
}
